import React, { useState, useEffect, useRef } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import VideoControls from './components/VideoControls';
import ChatPanel from './components/ChatPanel';
import NotesPanel from './components/NotesPanel';
import DocumentPanel from './components/DocumentPanel';
import SecurityStatus from './components/SecurityStatus';
import ConsultationTimer from './components/ConsultationTimer';

const VideoConsultation = () => {
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [consultationStartTime, setConsultationStartTime] = useState(null);
  const [billingRate] = useState(15000); // KES per hour
  const [clientConsent, setClientConsent] = useState(false);
  const [screenShareError, setScreenShareError] = useState(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);

  const consultationData = {
    clientName: "Sarah Wanjiku",
    caseNumber: "LDK-2024-0156",
    consultationType: "Initial Consultation",
    scheduledDuration: 60, // minutes
    lawyerName: "Advocate James Mwangi"
  };

  useEffect(() => {
    initializeVideoCall();
    setConsultationStartTime(new Date());
    
    return () => {
      cleanupVideoCall();
    };
  }, []);

  const initializeVideoCall = async () => {
    try {
      setConnectionStatus('connecting');
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('Your browser does not support video calls. Please use a modern browser like Chrome, Firefox, or Edge.');
      }
      
      // Get user media (camera and microphone)
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      
      localStreamRef.current = stream;
      if (localVideoRef?.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      setConnectionStatus('connected');
      
      // Simulate remote stream connection
      setTimeout(() => {
        setConnectionStatus('active');
      }, 2000);
      
    } catch (error) {
      console.error('Error accessing media devices:', error);
      
      let errorMessage = 'Failed to access camera/microphone. ';
      
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera and microphone access in your browser settings.';
      } else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera or microphone found. Please connect a device.';
      } else if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') {
        errorMessage += 'Camera or microphone is already in use by another application.';
      } else if (error?.message) {
        errorMessage += error?.message;
      } else {
        errorMessage += 'Please check your device settings and try again.';
      }
      
      alert(errorMessage);
      setConnectionStatus('error');
    }
  };

  const cleanupVideoCall = () => {
    if (localStreamRef?.current) {
      localStreamRef?.current?.getTracks()?.forEach(track => {
        track?.stop();
        track.enabled = false;
      });
      localStreamRef.current = null;
    }
    
    if (localVideoRef?.current) {
      localVideoRef.current.srcObject = null;
    }
    
    if (remoteVideoRef?.current) {
      remoteVideoRef.current.srcObject = null;
    }
  };

  const toggleVideo = () => {
    if (localStreamRef?.current) {
      const videoTrack = localStreamRef?.current?.getVideoTracks()?.[0];
      if (videoTrack) {
        videoTrack.enabled = !isVideoEnabled;
        setIsVideoEnabled(!isVideoEnabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStreamRef?.current) {
      const audioTrack = localStreamRef?.current?.getAudioTracks()?.[0];
      if (audioTrack) {
        audioTrack.enabled = !isAudioEnabled;
        setIsAudioEnabled(!isAudioEnabled);
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      setScreenShareError(null); // Clear any previous errors
      
      if (!isScreenSharing) {
        // Check if getDisplayMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices?.getDisplayMedia) {
          throw new Error('Screen sharing is not supported in this browser. Please use Chrome, Firefox, or Edge.');
        }

        // Check if we're in a secure context (HTTPS)
        if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
          throw new Error('Screen sharing requires a secure connection (HTTPS).');
        }

        const screenStream = await navigator.mediaDevices?.getDisplayMedia({
          video: {
            mediaSource: 'screen',
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 15, max: 30 }
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          }
        });
        
        // Handle screen share end detection
        screenStream?.getVideoTracks()?.[0]?.addEventListener('ended', () => {
          setIsScreenSharing(false);
          setScreenShareError(null);
          // Switch back to camera automatically
          switchToCameraStream();
        });
        
        // Replace video track with screen share
        const videoTrack = screenStream?.getVideoTracks()?.[0];
        if (localStreamRef?.current && videoTrack) {
          // In a real WebRTC implementation, you would replace the track in the peer connection
          const oldTrack = localStreamRef?.current?.getVideoTracks()?.[0];
          if (oldTrack) {
            localStreamRef?.current?.removeTrack(oldTrack);
            oldTrack?.stop();
          }
          localStreamRef?.current?.addTrack(videoTrack);
          
          if (localVideoRef?.current) {
            localVideoRef.current.srcObject = localStreamRef?.current;
          }
          
          setIsScreenSharing(true);
        }
      } else {
        // Switch back to camera
        await switchToCameraStream();
      }
    } catch (error) {
      console.error('Error with screen sharing:', error);
      
      let errorMessage = 'Failed to start screen sharing. ';
      
      if (error?.name === 'NotAllowedError') {
        errorMessage += 'Permission was denied. Please allow screen sharing and try again.';
      } else if (error?.name === 'NotFoundError') {
        errorMessage += 'No screen sharing source was selected.';
      } else if (error?.name === 'NotSupportedError') {
        errorMessage += 'Screen sharing is not supported in this browser.';
      } else if (error?.name === 'NotReadableError') {
        errorMessage += 'Screen sharing is currently in use by another application.';
      } else if (error?.name === 'AbortError') {
        errorMessage += 'Screen sharing was cancelled.';
      } else if (error?.message?.includes('secure connection')) {
        errorMessage += 'Screen sharing requires HTTPS connection.';
      } else if (error?.message?.includes('not supported')) {
        errorMessage += error?.message;
      } else {
        errorMessage += 'Please check your browser settings and try again.';
      }
      
      setScreenShareError(errorMessage);
      setIsScreenSharing(false);
    }
  };

  const switchToCameraStream = async () => {
    try {
      // Get new camera stream
      const cameraStream = await navigator.mediaDevices?.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      
      // Stop current tracks
      if (localStreamRef?.current) {
        localStreamRef?.current?.getTracks()?.forEach(track => track?.stop());
      }
      
      // Replace with camera stream
      localStreamRef.current = cameraStream;
      if (localVideoRef?.current) {
        localVideoRef.current.srcObject = cameraStream;
      }
      
      setIsScreenSharing(false);
      setScreenShareError(null);
      
    } catch (error) {
      console.error('Error switching back to camera:', error);
      setScreenShareError('Failed to switch back to camera. Please refresh the page.');
    }
  };

  const toggleRecording = () => {
    if (!clientConsent && !isRecording) {
      alert('Client consent is required before starting recording.');
      return;
    }
    
    setIsRecording(!isRecording);
    // In a real implementation, this would start/stop the recording
  };

  const endConsultation = () => {
    if (window.confirm('Are you sure you want to end this consultation?')) {
      cleanupVideoCall();
      // Navigate back to dashboard or consultation summary
      window.location.href = '/dashboard';
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connecting': return 'text-warning-600';
      case 'connected': return 'text-primary';
      case 'active': return 'text-success';
      case 'error': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Connected';
      case 'active': return 'Active Call';
      case 'error': return 'Connection Error';
      default: return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="md:ml-64 transition-layout flex flex-col h-screen">
        {/* Header */}
        <header className="bg-surface border-b border-border px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-xl font-heading font-bold text-text-primary">
                  Video Consultation
                </h1>
                <p className="text-sm text-text-secondary">
                  {consultationData?.clientName} • Case: {consultationData?.caseNumber}
                </p>
              </div>
              <div className={`flex items-center space-x-2 text-sm ${getConnectionStatusColor()}`}>
                <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
                <span>{getConnectionStatusText()}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <ConsultationTimer 
                startTime={consultationStartTime}
                billingRate={billingRate}
              />
              <SecurityStatus />
              <button
                onClick={endConsultation}
                className="px-4 py-2 bg-error text-white rounded-interactive hover:bg-error-dark transition-smooth"
              >
                End Call
              </button>
            </div>
          </div>
        </header>

        {/* Main Video Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video Section */}
          <div className="flex-1 relative bg-secondary-900">
            {/* Remote Video (Client) */}
            <div className="absolute inset-0">
              <div className="w-full h-full bg-secondary-800 flex items-center justify-center">
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                {connectionStatus !== 'active' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <div className="w-24 h-24 bg-secondary-700 rounded-full flex items-center justify-center mb-4">
                      <Icon name="User" size={40} />
                    </div>
                    <p className="text-lg font-medium">{consultationData?.clientName}</p>
                    <p className="text-sm text-secondary-300">
                      {connectionStatus === 'connecting' ? 'Joining...' : 'Camera Off'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Local Video (Lawyer) - Picture in Picture */}
            <div className="absolute top-4 right-4 w-64 h-48 bg-secondary-800 rounded-lg overflow-hidden shadow-lg border-2 border-white/20">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {!isVideoEnabled && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-secondary-800">
                  <div className="w-12 h-12 bg-secondary-700 rounded-full flex items-center justify-center mb-2">
                    <Icon name="User" size={20} />
                  </div>
                  <p className="text-xs">Camera Off</p>
                </div>
              )}
              
              {/* Local Video Controls */}
              <div className="absolute bottom-2 left-2 right-2 flex items-center justify-center space-x-2">
                <button
                  onClick={toggleVideo}
                  className={`p-2 rounded-full transition-smooth ${
                    isVideoEnabled 
                      ? 'bg-white/20 hover:bg-white/30' :'bg-error hover:bg-error-dark'
                  }`}
                >
                  <Icon 
                    name={isVideoEnabled ? "Video" : "VideoOff"} 
                    size={14} 
                    color="white" 
                  />
                </button>
                <button
                  onClick={toggleAudio}
                  className={`p-2 rounded-full transition-smooth ${
                    isAudioEnabled 
                      ? 'bg-white/20 hover:bg-white/30' :'bg-error hover:bg-error-dark'
                  }`}
                >
                  <Icon 
                    name={isAudioEnabled ? "Mic" : "MicOff"} 
                    size={14} 
                    color="white" 
                  />
                </button>
              </div>
            </div>

            {/* Screen Sharing Indicator */}
            {isScreenSharing && (
              <div className="absolute top-4 left-4 bg-primary text-white px-3 py-2 rounded-lg flex items-center space-x-2">
                <Icon name="Monitor" size={16} />
                <span className="text-sm font-medium">Screen Sharing Active</span>
              </div>
            )}

            {/* Screen Share Error Alert */}
            {screenShareError && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-error text-white px-4 py-3 rounded-lg shadow-lg max-w-md">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium mb-1">Screen Sharing Error</p>
                    <p className="text-xs">{screenShareError}</p>
                    <button
                      onClick={() => setScreenShareError(null)}
                      className="text-xs underline hover:no-underline mt-1"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Side Panels */}
          <div className="w-80 bg-surface border-l border-border flex flex-col">
            {/* Panel Tabs */}
            <div className="flex border-b border-border">
              <button
                onClick={() => {
                  setShowChat(true);
                  setShowNotes(false);
                  setShowDocuments(false);
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
                  showChat 
                    ? 'bg-primary-50 text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="MessageSquare" size={16} className="inline mr-2" />
                Chat
              </button>
              <button
                onClick={() => {
                  setShowChat(false);
                  setShowNotes(true);
                  setShowDocuments(false);
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
                  showNotes 
                    ? 'bg-primary-50 text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="FileText" size={16} className="inline mr-2" />
                Notes
              </button>
              <button
                onClick={() => {
                  setShowChat(false);
                  setShowNotes(false);
                  setShowDocuments(true);
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
                  showDocuments 
                    ? 'bg-primary-50 text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="Folder" size={16} className="inline mr-2" />
                Files
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-hidden">
              {showChat && <ChatPanel />}
              {showNotes && <NotesPanel caseNumber={consultationData?.caseNumber} />}
              {showDocuments && <DocumentPanel caseNumber={consultationData?.caseNumber} />}
            </div>
          </div>
        </div>

        {/* Bottom Controls */}
        <VideoControls
          isVideoEnabled={isVideoEnabled}
          isAudioEnabled={isAudioEnabled}
          isScreenSharing={isScreenSharing}
          isRecording={isRecording}
          clientConsent={clientConsent}
          screenShareError={screenShareError}
          onToggleVideo={toggleVideo}
          onToggleAudio={toggleAudio}
          onToggleScreenShare={toggleScreenShare}
          onToggleRecording={toggleRecording}
          onEndCall={endConsultation}
          onClientConsentChange={setClientConsent}
        />
      </div>
    </div>
  );
};

export default VideoConsultation;